import { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useCategories } from '../hooks/useCategories';
import { today } from '../lib/dateUtils';
import { t } from '../lib/i18n';
import { getOrCreateShareToken, shareRecord } from '../lib/kakaoShare';
import EmojiPicker from '../components/EmojiPicker';
import '../components/EmojiPicker.css';
import PhotoViewer from '../components/PhotoViewer';
import './AddRecordPage.css';

function compressImage(file, maxSize = 1280, quality = 0.8) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
        'image/jpeg',
        quality
      );
    };
    img.src = URL.createObjectURL(file);
  });
}

const DEFAULT_EMOJI_MAP = {
  '거주지': '🏠', '자동차': '🚗', '핸드폰': '📱', '시계': '⌚',
  '학교': '🏫', '직장': '💼', '여행': '✈️', '물건': '📦', '사건': '⭐',
};

const FALLBACK_CATEGORIES = [
  { name: '거주지', emoji: '🏠' }, { name: '자동차', emoji: '🚗' }, { name: '핸드폰', emoji: '📱' },
  { name: '시계',   emoji: '⌚' }, { name: '학교',   emoji: '🏫' }, { name: '직장',   emoji: '💼' },
  { name: '여행',   emoji: '✈️' }, { name: '물건',   emoji: '📦' }, { name: '사건',   emoji: '⭐' },
];

function getCatEmoji(cat) {
  return cat.icon || DEFAULT_EMOJI_MAP[cat.name] || '📦';
}

// record prop이 있으면 수정 모드, 없으면 추가 모드
export default function AddRecordPage({ user, record, onBack, onDeleted, currentProfile, profiles }) {
  const isEditMode = !!record;
  const { categories, loading: catLoading } = useCategories(user.id);
  const allProfiles = profiles || [];
  const displayCategories = categories.length > 0 ? categories : FALLBACK_CATEGORIES;

  // 모든 프로필을 공유 목록에 표시 (현재 프로필 포함)

  // 수정 모드면 기존 카테고리로, 추가 모드면 null로 시작
  const [selectedCat, setSelectedCat] = useState(
    isEditMode && record.category_name ? { name: record.category_name, icon: record.category_emoji || '' } : null
  );
  const [userSelected, setUserSelected] = useState(isEditMode);

  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customEmoji, setCustomEmoji] = useState('⭐');

  const [title, setTitle] = useState(record?.title ?? '');
  const [startDate, setStartDate] = useState(record?.start_date ?? today());
  const [endDate, setEndDate] = useState(record?.end_date ?? '');
  const [isOngoing, setIsOngoing] = useState(record?.is_ongoing ?? false);
  const [isSameDay, setIsSameDay] = useState(
    isEditMode && record?.start_date && record?.end_date && record.start_date === record.end_date && !record.is_ongoing
  );
  const [locationName, setLocationName] = useState(record?.location_name ?? '');
  const [latitude, setLatitude] = useState(record?.latitude ?? null);
  const [longitude, setLongitude] = useState(record?.longitude ?? null);
  const [locQuery, setLocQuery] = useState('');
  const [locResults, setLocResults] = useState([]);

  const [existingPhotos, setExistingPhotos] = useState(
    Array.isArray(record?.photos) ? record.photos : []
  );
  const [photos, setPhotos] = useState([]);
  const [memo, setMemo] = useState(record?.memo ?? '');
  // 추가 모드: 현재 프로필 기본 ON / 수정 모드: DB에서 로드
  const [sharedProfileIds, setSharedProfileIds] = useState(
    isEditMode ? [] : (currentProfile?.id ? [currentProfile.id] : [])
  );
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [uploadErrors, setUploadErrors] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [sharing, setSharing] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // 수정 모드: 기존 공유 설정 불러오기
  useEffect(() => {
    if (isEditMode && record.id) {
      supabase
        .from('record_shares')
        .select('profile_id')
        .eq('record_id', record.id)
        .then(({ data }) => {
          setSharedProfileIds(data?.map((s) => s.profile_id) || []);
        });
    }
  }, [isEditMode, record?.id]);

  const handleDelete = async () => {
    if (!window.confirm(t('confirm_delete'))) return;
    setDeleting(true);
    const { error } = await supabase.from('records').delete().eq('id', record.id);
    if (error) {
      alert('삭제 실패: ' + error.message);
      setDeleting(false);
    } else {
      onDeleted();
    }
  };

  // 추가 모드일 때만 첫 번째 카테고리 자동 선택
  useEffect(() => {
    if (!userSelected && displayCategories.length > 0) {
      setSelectedCat(displayCategories[0]);
    }
  }, [displayCategories, userSelected]);

  const selectChip = (cat) => {
    setUserSelected(true);
    setSelectedCat({ ...cat });
    setCustomMode(false);
    setCustomName('');
    setCustomEmoji('⭐');
  };

  const selectCustomMode = () => {
    setUserSelected(true);
    setSelectedCat(null);
    setCustomMode(true);
  };

  const searchPlaces = () => {
    if (!locQuery.trim() || !window.kakao?.maps?.services) return;
    const ps = new window.kakao.maps.services.Places();
    ps.keywordSearch(locQuery, (result, status) => {
      if (status === window.kakao.maps.services.Status.OK) {
        setLocResults(result.slice(0, 5));
      } else {
        setLocResults([]);
      }
    });
  };

  const selectPlace = (place) => {
    setLocationName(place.place_name);
    setLatitude(parseFloat(place.y));
    setLongitude(parseFloat(place.x));
    setLocResults([]);
    setLocQuery('');
  };

  const clearLocation = () => {
    setLocationName('');
    setLatitude(null);
    setLongitude(null);
  };

  const totalPhotoCount = existingPhotos.length + photos.length;

  const handlePhotoAdd = async (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - totalPhotoCount;
    const selected = files.slice(0, remaining);
    const newPhotos = [];
    for (const file of selected) {
      const compressed = await compressImage(file);
      newPhotos.push({ file: compressed, preview: URL.createObjectURL(compressed) });
    }
    setPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = '';
  };

  const handleExistingPhotoRemove = (index) => {
    setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewPhotoRemove = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPhoto = async (file) => {
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
      .from('record-photos')
      .upload(path, file, { contentType: file.type });
    if (error) throw error;
    const { data } = supabase.storage.from('record-photos').getPublicUrl(path);
    return data.publicUrl;
  };

  const toggleShareProfile = async (profileId) => {
    const isShared = sharedProfileIds.includes(profileId);

    // 수정 모드: 즉시 DB에 반영
    if (isEditMode && record.id) {
      if (isShared) {
        await supabase
          .from('record_shares')
          .delete()
          .eq('record_id', record.id)
          .eq('profile_id', profileId);
      } else {
        await supabase
          .from('record_shares')
          .insert({ record_id: record.id, profile_id: profileId });
      }
    }

    setSharedProfileIds((prev) =>
      isShared ? prev.filter((id) => id !== profileId) : [...prev, profileId]
    );
  };

  const handleShare = async () => {
    if (!record?.id) return;
    setSharing(true);
    try {
      const token = await getOrCreateShareToken(record.id, user.id);
      const method = await shareRecord(record, token);
      if (method === 'clipboard') {
        alert('공유 링크가 클립보드에 복사되었습니다!');
      }
    } catch (e) {
      alert('공유 링크 생성 실패: ' + e.message);
    } finally {
      setSharing(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!startDate) {
      alert('시작일을 선택해주세요.');
      return;
    }

    const catObj = customMode
      ? { name: customName.trim(), icon: customEmoji }
      : selectedCat;

    if (!catObj || !catObj.name) {
      alert('카테고리를 선택해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    setUploadErrors([]);

    // 직접 입력 카테고리: categories 테이블에 없으면 신규 추가
    if (customMode && catObj.name) {
      const { data: existing } = await supabase
        .from('categories')
        .select('id')
        .eq('user_id', user.id)
        .eq('name', catObj.name)
        .maybeSingle();
      if (!existing) {
        await supabase.from('categories').insert({
          user_id: user.id,
          name: catObj.name,
          icon: catObj.icon,
          sort_order: categories.length,
        });
      }
    }

    const newUrls = [];
    const failedNames = [];
    for (const photo of photos) {
      try {
        newUrls.push(await uploadPhoto(photo.file));
      } catch (e) {
        failedNames.push(photo.file.name);
      }
    }
    if (failedNames.length > 0) {
      setUploadErrors(failedNames);
    }
    const allPhotos = [...existingPhotos, ...newUrls];

    const payload = {
      category_name: catObj.name,
      category_emoji: getCatEmoji(catObj),
      title: title.trim(),
      start_date: startDate,
      end_date: isOngoing ? null : isSameDay ? startDate : (endDate || null),
      is_ongoing: isOngoing,
      memo: memo.trim() || null,
      photos: allPhotos,
      location_name: locationName || null,
      latitude: latitude || null,
      longitude: longitude || null,
    };

    let err;
    let savedRecordId = record?.id;

    if (isEditMode) {
      ({ error: err } = await supabase.from('records').update(payload).eq('id', record.id));
    } else {
      const { data: newRecord, error: insertErr } = await supabase
        .from('records')
        .insert({ ...payload, user_id: user.id, profile_id: currentProfile?.id || null })
        .select()
        .single();
      err = insertErr;
      savedRecordId = newRecord?.id;
    }

    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }

    // 공유 설정 저장 (추가 모드에서만 — 수정 모드는 토글 시 실시간 저장)
    if (!isEditMode && savedRecordId && sharedProfileIds.length > 0) {
      await supabase.from('record_shares').insert(
        sharedProfileIds.map((pid) => ({ record_id: savedRecordId, profile_id: pid }))
      );
    }

    onBack();
  };

  return (
    <div className="add-record-page">
      <header className="add-record-header">
        <button className="header-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>{isEditMode ? t('record_edit') : t('record_add')}</h2>
        <div className="header-spacer" />
      </header>

      <div className="add-record-body">
        {error && <p className="form-error">{error}</p>}

        {/* 카테고리 */}
        <section className="form-section">
          <label className="section-label">
            {t('record_category')}
            {catLoading && <span className="section-label-sub"> {t('home_loading')}</span>}
          </label>
          <div className="category-chips">
            {displayCategories.map((cat) => {
              const isActive = !customMode && selectedCat && (
                cat.id ? cat.id === selectedCat.id : cat.name === selectedCat.name
              );
              return (
                <button
                  key={cat.id ?? cat.name}
                  type="button"
                  className={`category-chip ${isActive ? 'category-chip--active' : ''}`}
                  onClick={() => selectChip(cat)}
                >
                  <span className="chip-emoji">{getCatEmoji(cat)}</span>
                  {cat.name}
                </button>
              );
            })}
            <button
              type="button"
              className={`category-chip category-chip--custom ${customMode ? 'category-chip--active' : ''}`}
              onClick={selectCustomMode}
            >
              {t('record_custom_input')}
            </button>
          </div>

          {customMode && (
            <div className="custom-category-input">
              <input
                type="text"
                placeholder={t('record_custom_placeholder')}
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="custom-name-input"
              />
              <p className="custom-emoji-label">{t('record_emoji_select')}</p>
              <EmojiPicker value={customEmoji} onChange={setCustomEmoji} />
            </div>
          )}
        </section>

        {/* 제목 */}
        <section className="form-section">
          <label className="section-label" htmlFor="title">{t('record_title')}</label>
          <input
            id="title"
            type="text"
            placeholder={t('record_title_placeholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </section>

        {/* 날짜 */}
        <section className="form-section">
          <label className="section-label">{t('record_period')}</label>
          <div className="date-row">
            <div className="date-field">
              <span className="date-field-label">{t('record_start_date')}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-divider">→</div>
            <div className="date-field">
              <span className="date-field-label">{t('record_end_date')}</span>
              <input
                type="date"
                value={isSameDay ? startDate : endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isOngoing || isSameDay}
              />
            </div>
          </div>
          <div className="date-toggles">
            <label className="ongoing-toggle">
              <input
                type="checkbox"
                checked={isSameDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsSameDay(checked);
                  if (checked) {
                    setIsOngoing(false);
                    setEndDate(startDate);
                  } else {
                    setEndDate('');
                  }
                }}
              />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">{t('record_same_day')}</span>
            </label>
            <label className="ongoing-toggle">
              <input
                type="checkbox"
                checked={isOngoing}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsOngoing(checked);
                  if (checked) {
                    setIsSameDay(false);
                    setEndDate('');
                  }
                }}
              />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">{t('record_ongoing')}</span>
            </label>
          </div>
        </section>

        {/* 위치 */}
        <section className="form-section">
          <label className="section-label">{t('record_location')}</label>
          {locationName ? (
            <div className="location-selected">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
              <span>{locationName}</span>
              <button type="button" className="location-clear" onClick={clearLocation}>×</button>
            </div>
          ) : (
            <div className="location-search-wrap">
              <div className="location-search-row">
                <input
                  type="text"
                  placeholder={t('record_location_placeholder')}
                  value={locQuery}
                  onChange={(e) => setLocQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchPlaces()}
                  className="location-input"
                />
                <button type="button" className="location-search-btn" onClick={searchPlaces}>{t('record_search')}</button>
              </div>
              {locResults.length > 0 && (
                <ul className="location-results">
                  {locResults.map((place) => (
                    <li key={place.id} onClick={() => selectPlace(place)}>
                      <span className="place-name">{place.place_name}</span>
                      <span className="place-addr">{place.road_address_name || place.address_name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        {/* 사진 */}
        <section className="form-section">
          <label className="section-label">
            {t('record_photos')} <span className="section-label-sub">({totalPhotoCount}/5)</span>
          </label>
          {uploadErrors.length > 0 && (
            <p className="form-error">
              업로드 실패: {uploadErrors.join(', ')} — Storage 버킷 설정을 확인해주세요.
            </p>
          )}
          <div className="photo-grid">
            {existingPhotos.map((url, i) => (
              <div key={`existing-${i}`} className="photo-item">
                {i === 0 && photos.length === 0 && <span className="photo-badge-main">{t('record_main_photo')}</span>}
                <img
                  src={url}
                  alt={`사진 ${i + 1}`}
                  onClick={() => setViewerIndex(i)}
                />
                <button
                  type="button"
                  className="photo-remove"
                  onClick={() => handleExistingPhotoRemove(i)}
                >×</button>
              </div>
            ))}
            {photos.map((photo, i) => (
              <div key={`new-${i}`} className="photo-item">
                {i === 0 && existingPhotos.length === 0 && <span className="photo-badge-main">{t('record_main_photo')}</span>}
                <img
                  src={photo.preview}
                  alt={`새 사진 ${i + 1}`}
                  onClick={() => setViewerIndex(existingPhotos.length + i)}
                />
                <button
                  type="button"
                  className="photo-remove"
                  onClick={() => handleNewPhotoRemove(i)}
                >×</button>
              </div>
            ))}
            {totalPhotoCount < 5 && (
              <button
                type="button"
                className="photo-add"
                onClick={() => galleryInputRef.current?.click()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}
          </div>
          {/* 갤러리 선택용 */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handlePhotoAdd}
          />
          {/* 카메라 촬영용 */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handlePhotoAdd}
          />
        </section>

        {/* 메모 */}
        <section className="form-section">
          <label className="section-label" htmlFor="memo">{t('record_memo')}</label>
          <textarea
            id="memo"
            placeholder={t('record_memo_placeholder')}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            rows={4}
          />
        </section>

        {/* 공유 */}
        {allProfiles.length > 0 && (
          <section className="form-section">
            <label className="section-label">{t('record_profiles')}</label>
            <div className="share-profiles">
              {allProfiles.map((p) => {
                const isCurrent = p.id === currentProfile?.id;
                return (
                  <label key={p.id} className="share-profile-row">
                    <span className="share-profile-info">
                      <span className="share-profile-emoji">{p.emoji}</span>
                      <span className="share-profile-name">{p.name}</span>
                      {isCurrent && <span className="share-profile-tag">{t('current')}</span>}
                    </span>
                    <span className="share-toggle">
                      <input
                        type="checkbox"
                        checked={sharedProfileIds.includes(p.id)}
                        onChange={() => toggleShareProfile(p.id)}
                      />
                      <span className="toggle-track">
                        <span className="toggle-thumb" />
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {isEditMode && (
        <div className="kakao-share-section">
          <button className="btn-kakao-share" onClick={handleShare} disabled={sharing}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            {sharing ? t('record_sharing') : t('record_share')}
          </button>
        </div>
      )}

      <div className="add-record-footer">
        {isEditMode && (
          <button className="btn-delete-record" onClick={handleDelete} disabled={deleting || loading}>
            {deleting ? t('record_deleting') : t('record_delete')}
          </button>
        )}
        <button className="btn-save" onClick={handleSave} disabled={loading || deleting}>
          {loading ? t('record_saving') : t('record_save')}
        </button>
      </div>

      {/* 사진 전체화면 뷰어 */}
      {viewerIndex !== null && (
        <PhotoViewer
          photos={[...existingPhotos, ...photos.map((p) => p.preview)]}
          startIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}
